/*
 * Payroll, time and My pay (draft 4): request and answer shapes. Money fields end in Cents and are whole cents. My pay
 * answers carry only the person's own share, never a visit's price or anyone else's pay.
 */
import { z } from "zod";
import { dateString } from "./clients.zod.js";

const timeString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Type a time like 08:00 or 13:30.");
const optionalNote = z
  .string()
  .trim()
  .max(300)
  .transform((s) => (s === "" ? null : s))
  .nullable()
  .optional();

export const payWeekQuery = z.object({ start: dateString.optional() });

/** Mark paid. early: the office saw the warning that the period has not ended and means to pay it now (question 48). */
export const markPaidBody = z
  .object({
    early: z.boolean().default(false),
    /** The office saw that some jobs this week have no crew yet and means to pay without them (question 57). */
    noCrewOk: z.boolean().default(false),
  })
  .strict();
export type MarkPaidBody = z.infer<typeof markPaidBody>;

/** Whether the signed-in person has My pay: on the team list (linked) and their My pay switch on (questions 49, 50). */
export const myPayAccess = z.object({ linked: z.boolean(), on: z.boolean() });
export type MyPayAccess = z.infer<typeof myPayAccess>;

// People's pay ---------------------------------------------------------------------------------------------------

export const payPersonRow = z.object({
  id: z.uuid(),
  name: z.string(),
  role: z.string(),
  active: z.boolean(),
  payType: z.enum(["percent", "hourly"]),
  percent: z.number().nullable(),
  hourlyRateCents: z.number().nullable(),
});
export type PayPersonRow = z.infer<typeof payPersonRow>;

export const payPatch = z
  .object({
    payType: z.enum(["percent", "hourly"]),
    percent: z.number().min(0, "Use 0 to 100.").max(100, "Use 0 to 100.").nullable(),
    hourlyRateCents: z.number().int().min(0).max(100_000).nullable(),
  })
  .partial()
  .strict();
export type PayPatch = z.infer<typeof payPatch>;

// Shifts ---------------------------------------------------------------------------------------------------------

/** Where the phone was at a clock in or out, when the phone allowed it. accuracyM: how sure the phone was, in meters. */
export const clockWhere = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().int().min(0).max(100_000).nullable(),
});
export type ClockWhere = z.infer<typeof clockWhere>;

/** Clock in or out from the phone: where it was, or nothing when location is off. */
export const clockBody = z.object({ where: clockWhere.nullable().default(null) }).strict();
export type ClockBody = z.infer<typeof clockBody>;

export const shiftRow = z.object({
  id: z.uuid(),
  personId: z.uuid(),
  personName: z.string(),
  date: z.string(),
  /** ISO instants. */
  clockIn: z.string(),
  clockOut: z.string().nullable(),
  /** Company-local wall times, for the screens. */
  inTime: z.string(),
  outTime: z.string().nullable(),
  minutes: z.number(),
  source: z.string(),
  edited: z.boolean(),
  note: z.string().nullable(),
  /** Where the phone was at clock in and out (question 51). Null when typed by the office or location was off. */
  inWhere: clockWhere.nullable(),
  outWhere: clockWhere.nullable(),
});
export type ShiftRow = z.infer<typeof shiftRow>;

/** One thing that happened in a shift, for HR > Time's activity log. */
export const shiftEvent = z.object({
  /** ISO instant. */
  at: z.string(),
  /** Company-local wall time. */
  time: z.string(),
  kind: z.enum(["in", "out", "now", "onmyway", "start", "pause", "done", "photos", "checklist", "office"]),
  text: z.string(),
  /** The visit it was on, to open it. */
  visitRef: z.string().nullable(),
  where: clockWhere.nullable(),
  /** Done before clocking in or after clocking out. */
  off: z.boolean(),
});
export type ShiftEvent = z.infer<typeof shiftEvent>;

/** The office adds or fixes time. Times are company-local on the day given; a blank end means still on the clock. */
export const shiftInput = z.object({
  personId: z.uuid(),
  date: dateString,
  inTime: timeString,
  outTime: timeString.nullable().optional(),
  note: optionalNote,
});
export type ShiftInput = z.infer<typeof shiftInput>;

export const shiftQuery = z.object({ from: dateString, to: dateString, personId: z.uuid().optional() });

export const clockAnswer = z.object({
  /** Is this person on the clock now. */
  on: z.boolean(),
  shift: shiftRow.nullable(),
  /** Minutes clocked today, the open shift included. */
  todayMinutes: z.number(),
  /** False when the login is not on the team list, so there is nobody to clock. */
  linked: z.boolean(),
  /** Send where the phone is while on the clock (Settings, Live GPS). */
  liveGps: z.boolean(),
});
export type ClockAnswer = z.infer<typeof clockAnswer>;

// The payroll week -----------------------------------------------------------------------------------------------

const jobShare = z.object({ personId: z.string(), payCents: z.number(), tipCents: z.number(), heldCents: z.number() });
const payJob = z.object({
  visitId: z.string(),
  date: z.string(),
  paidOn: z.string(),
  clientName: z.string(),
  baseCents: z.number(),
  tipCents: z.number(),
  poolPercent: z.number(),
  raised: z.boolean(),
  kept: z.boolean(),
  commercial: z.boolean().optional(),
  poolPayCents: z.number(),
  shares: z.array(jobShare),
  /** An invoice typed by hand: visitId is the invoice's id. */
  byHand: z.boolean().optional(),
});
const lateRow = z.object({
  kind: z.enum(["late", "change", "hours"]),
  from: z.string(),
  visitId: z.string().nullable(),
  personId: z.string(),
  payCents: z.number(),
  tipCents: z.number(),
  hoursCents: z.number(),
  date: z.string().nullable(),
  clientName: z.string().nullable(),
  cash: z.boolean(),
  byHand: z.boolean().optional(),
  carriesOn: z.boolean().optional(),
});
const payLine = z.object({
  personId: z.string(),
  name: z.string(),
  payType: z.enum(["percent", "hourly"]),
  jobs: z.number(),
  jobPayCents: z.number(),
  tipCents: z.number(),
  heldCents: z.number(),
  hourlyPayCents: z.number().nullable(),
  minutes: z.number(),
  active: z.boolean(),
  ownCents: z.number(),
  carryCents: z.number(),
  carryFrom: z.string().nullable(),
  lateCents: z.number(),
  totalCents: z.number(),
});

export const payWeekAnswer = z.object({
  start: z.string(),
  end: z.string(),
  payday: z.string(),
  /** paid, current (today is in it), open (past, not paid) or future. */
  state: z.enum(["paid", "current", "open", "future"]),
  /** How long this pay period is: 7 or 14 days. */
  days: z.number(),
  /** Not paid and not ended yet: marking it paid now is paying early, so the screen warns first. */
  early: z.boolean(),
  paid: z.object({ paidAt: z.string(), paidBy: z.string().nullable(), nextStart: z.string() }).nullable(),
  /** A later paid week that holds money from this one; undo that first. */
  undoBlockedBy: z.string().nullable(),
  people: z.array(payLine),
  jobs: z.array(payJob),
  late: z.array(lateRow),
  noCrew: z.array(
    z.object({
      visitId: z.string(),
      date: z.string(),
      paidOn: z.string(),
      clientName: z.string(),
      baseCents: z.number(),
      tipCents: z.number(),
      fromStart: z.string().nullable(),
      byHand: z.boolean().optional(),
    }),
  ),
  totalCents: z.number(),
  /** Done visits not paid yet, and what each share would be if they all were. */
  waiting: z.object({
    count: z.number(),
    baseCents: z.number(),
    noCrewCents: z.number(),
    people: z.array(
      z.object({ personId: z.string(), name: z.string(), jobs: z.number(), payCents: z.number(), hourly: z.boolean() }),
    ),
  }),
  /** The settings payroll uses, shown read only with a button to Settings. */
  settings: z.object({
    paydayWeekday: z.number(),
    defaultPoolPercent: z.number().nullable(),
    commercialPoolPercent: z.number().nullable(),
    periodWeeks: z.number(),
    twoWeekFrom: z.string().nullable(),
  }),
  /** People paid by the hour with no rate set. */
  missingRates: z.array(z.string()),
  shifts: z.array(shiftRow),
});
export type PayWeekAnswer = z.infer<typeof payWeekAnswer>;
export type PayWeekLine = z.infer<typeof payLine>;

// My pay ---------------------------------------------------------------------------------------------------------

const myPayWeek = z.object({
  start: z.string(),
  end: z.string(),
  payday: z.string(),
  state: z.enum(["paid", "owes", "open", "current"]),
  amountCents: z.number(),
  paidAt: z.string().nullable(),
});
export const myPayList = z.object({ linked: z.boolean(), weeks: z.array(myPayWeek) });
export type MyPayList = z.infer<typeof myPayList>;

export const myPayAnswer = myPayWeek.extend({
  payType: z.enum(["percent", "hourly"]),
  jobPayCents: z.number(),
  tipCents: z.number(),
  hourlyPayCents: z.number().nullable(),
  minutes: z.number(),
  owedCents: z.number(),
  owedFrom: z.string().nullable(),
  nextStart: z.string().nullable(),
  lines: z.array(
    z.object({
      kind: z.enum(["job", "late", "change", "hours"]),
      from: z.string().nullable(),
      date: z.string().nullable(),
      clientName: z.string().nullable(),
      payCents: z.number(),
      tipCents: z.number(),
      cash: z.boolean(),
    }),
  ),
});
export type MyPayAnswer = z.infer<typeof myPayAnswer>;
